import { buildPdf, type PdfImage } from '../../../shared/pdf.js';
import {
  BINGO_COLUMNS,
  FREE,
  ballLabel,
  bingoAfter,
  missingForStage,
  rowMissing,
  stageLabel,
  type BingoCard,
  type BingoLogEvent,
  type BingoLogView,
} from '../../../shared/bingo.js';

// The bingo log as a PDF (user request 2026-10-02, bingo-suunnitelma.md "Bingoloki"): A4 pages drawn on a
// canvas and saved with the small PDF writer of the code cards. The summary, the winners, the drawn
// numbers and every event; after the events of a number, the coupons of its bingo situations (winners,
// bingos nobody called, late presses).

const DPI = 170;
const PX = DPI / 25.4;
const px = (mm: number) => mm * PX;
const PAGE_W = 210;
const PAGE_H = 297;
const M = 14;
const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const COLUMN = ['#2563eb', '#ef4444', '#e5e7eb', '#22c55e', '#facc15'];
const TAG: Record<string, { label: string; bg: string; fg: string }> = {
  start: { label: 'Peli', bg: '#c4b5fd', fg: '#1a2233' },
  stage: { label: 'Peli', bg: '#c4b5fd', fg: '#1a2233' },
  end: { label: 'Peli', bg: '#c4b5fd', fg: '#1a2233' },
  draw: { label: 'Arvonta', bg: '#e0e7ff', fg: '#334' },
  bingo: { label: 'Bingo', bg: '#bbf7d0', fg: '#14532d' },
  unclaimed: { label: 'Huutamaton', bg: '#9ca3af', fg: '#fff' },
  old: { label: 'Vanheni', bg: '#d1d5db', fg: '#1a2233' },
  press: { label: 'BINGO!', bg: '#fde68a', fg: '#1a2233' },
  wrong: { label: 'Ei bingoa', bg: '#fecaca', fg: '#7f1d1d' },
  late: { label: 'Myöhässä', bg: '#fb923c', fg: '#fff' },
  win: { label: 'Voittaja', bg: '#22c55e', fg: '#fff' },
};

const clock = (ms: number) => new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const day = (ms: number) => new Date(ms).toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });
const who = (e: BingoLogEvent) => `${e.nickname ?? '?'} (kuponki ${e.cardNumber})`;

interface Picture {
  title: string;
  cells: BingoCard;
  drawn: number[];
  stage: number;
  /** Frame: winner green, uncalled grey dashed, late orange. */
  tone: 'win' | 'unclaimed' | 'late';
}

interface Row {
  at: number;
  tag: string;
  text: string;
  bold?: boolean;
  small?: boolean;
}

/** The texts of the events, and the pictures of each number's bingo situations. */
function describe(log: BingoLogView): { rows: (Row | Picture[])[]; winners: { stage: number; nickname: string; card: number; after: number; at: number; jackpot: boolean }[]; presses: number } {
  const drawsAt = (seq: number) => log.draws.slice(0, seq);
  const after = (e: BingoLogEvent) => (e.cells ? bingoAfter(e.cells, drawsAt(e.drawSeq), e.stage) : null);
  // A bingo is called when its coupon pressed (accepted / already / late) or won in that stage.
  const called = new Set(
    log.events.filter((e) => e.kind === 'win' || e.kind === 'late' || (e.kind === 'press' && e.detail !== 'wait')).map((e) => `${e.stage}:${e.cardNumber}`),
  );
  const rows: (Row | Picture[])[] = [];
  const winners: { stage: number; nickname: string; card: number; after: number; at: number; jackpot: boolean }[] = [];
  let pictures: Picture[] = [];
  let presses = 0;
  const flush = () => {
    if (pictures.length > 0) rows.push(pictures);
    pictures = [];
  };
  const picture = (e: BingoLogEvent, title: string, tone: Picture['tone']) =>
    e.cells && pictures.push({ title, cells: e.cells, drawn: drawsAt(e.drawSeq), stage: e.stage, tone });

  for (const e of log.events) {
    if (e.kind === 'draw' || e.kind === 'stage' || e.kind === 'end') flush();
    switch (e.kind) {
      case 'start':
        rows.push({
          at: e.at,
          tag: 'start',
          text: `Peli alkoi: ${log.mode === 'auto' ? 'automaattinen' : 'juonnettu'} bingo, ${log.labels}, ${log.coupons} kuponkia, ${log.players} pelaajaa. Haetaan: ${stageLabel(1)}.`,
        });
        break;
      case 'draw':
        rows.push({ at: e.at, tag: 'draw', text: `Arvottu ${e.drawSeq}. numero: ${ballLabel(e.number!)}`, small: true });
        break;
      case 'bingo': {
        const k = after(e) ?? e.drawSeq;
        const unclaimed = !called.has(`${e.stage}:${e.cardNumber}`);
        const how =
          e.detail === 'carried'
            ? `${stageLabel(e.stage)} valmiina jo vaiheen alkaessa (${k}. numerolla ${ballLabel(log.draws[k - 1]!)})`
            : `${stageLabel(e.stage)} täyttyi numerolla ${ballLabel(log.draws[k - 1]!)}`;
        rows.push({ at: e.at, tag: unclaimed ? 'unclaimed' : 'bingo', text: `${unclaimed ? 'Huutamaton bingo' : 'Bingo'}: ${who(e)} – ${how}.` });
        if (unclaimed) picture(e, `Huutamaton bingo – ${e.nickname}, kuponki ${e.cardNumber}`, 'unclaimed');
        break;
      }
      case 'old':
        rows.push({ at: e.at, tag: 'old', text: `${who(e)}: bingo vanheni – sitä ei huudettu ennen seuraavaa numeroa.` });
        break;
      case 'press':
        presses++;
        rows.push({
          at: e.at,
          tag: 'press',
          text:
            e.detail === 'already'
              ? `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – bingo oli jo ilmoitettu.`
              : e.detail === 'wait'
                ? `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – liian pian edellisen väärän painalluksen jälkeen.`
                : log.mode === 'host'
                  ? `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – oikea bingo, ilmoitus juontajalle.`
                  : `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – oikea bingo, tarkistetaan.`,
        });
        break;
      case 'wrong': {
        presses++;
        const missing = e.cells ? missingForStage(e.cells, new Set(drawsAt(e.drawSeq)), e.stage) : null;
        rows.push({
          at: e.at,
          tag: 'wrong',
          text: `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – ei bingoa${missing ? ` (${stageLabel(e.stage)}: puuttuu ${missing} ${missing === 1 ? 'numero' : 'numeroa'})` : ''}.`,
        });
        break;
      }
      case 'late': {
        presses++;
        const k = after(e);
        rows.push({
          at: e.at,
          tag: 'late',
          text: `${e.nickname} painoi BINGO! (kuponki ${e.cardNumber}) – myöhässä: vanha bingo${k ? ` (${k}. numerolla)` : ''}, uudempi bingo voittaa.`,
        });
        picture(e, `Myöhässä (vanha bingo) – ${e.nickname}, kuponki ${e.cardNumber}`, 'late');
        break;
      }
      case 'win': {
        const k = after(e) ?? e.drawSeq;
        const jackpot = e.detail === 'jackpot';
        winners.push({ stage: e.stage, nickname: e.nickname ?? '?', card: e.cardNumber ?? 0, after: k, at: e.at, jackpot });
        rows.push({ at: e.at, tag: 'win', bold: true, text: `${stageLabel(e.stage)}: voittaja ${who(e)} – ${k}. numerolla.${jackpot ? ' SUPERJACKPOT!' : ''}` });
        picture(e, `Voittaja – ${e.nickname}, kuponki ${e.cardNumber}`, 'win');
        break;
      }
      case 'stage':
        rows.push({ at: e.at, tag: 'stage', text: `Siirrytään vaiheeseen: ${stageLabel(e.stage)}.` });
        break;
      case 'end':
        rows.push({
          at: e.at,
          tag: 'end',
          text:
            e.detail === 'full'
              ? `Peli päättyi: täysi kuponki löytyi. Arvottu ${e.drawSeq}/75.`
              : `Peli lopetettiin ennen täyttä kupongia. Arvottu ${e.drawSeq}/75.`,
        });
        break;
    }
  }
  flush();
  return { rows, winners, presses };
}

class Pages {
  pages: HTMLCanvasElement[] = [];
  ctx!: CanvasRenderingContext2D;
  y = M;

  constructor() {
    this.add();
  }

  add() {
    const c = document.createElement('canvas');
    c.width = Math.round(px(PAGE_W));
    c.height = Math.round(px(PAGE_H));
    this.ctx = c.getContext('2d')!;
    this.ctx.fillStyle = '#fff';
    this.ctx.fillRect(0, 0, c.width, c.height);
    this.ctx.textBaseline = 'top';
    this.pages.push(c);
    this.y = M;
  }

  ensure(h: number) {
    if (this.y + h > PAGE_H - M) this.add();
  }

  font(size: number, weight = '400') {
    this.ctx.font = `${weight} ${px(size)}px ${SANS}`;
  }

  wrap(text: string, width: number): string[] {
    const lines: string[] = [];
    let line = '';
    for (const word of text.split(' ')) {
      const next = line ? `${line} ${word}` : word;
      if (this.ctx.measureText(next).width > px(width) && line) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    if (line) lines.push(line);
    return lines;
  }

  text(t: string, x: number, y: number, color = '#1a2233') {
    this.ctx.fillStyle = color;
    this.ctx.fillText(t, px(x), px(y));
  }

  rect(x: number, y: number, w: number, h: number, fill: string, r = 0, stroke?: string, dash = false) {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.roundRect(px(x), px(y), px(w), px(h), px(r));
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.setLineDash(dash ? [px(1.2), px(0.8)] : []);
      ctx.strokeStyle = stroke;
      ctx.lineWidth = px(0.35);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  heading(t: string) {
    this.ensure(14);
    this.y += 3;
    this.font(5, '700');
    this.text(t, M, this.y);
    this.y += 6.5;
    this.rect(M, this.y, PAGE_W - 2 * M, 0.6, '#7c5cf0');
    this.y += 3;
  }
}

function drawCoupon(p: Pages, pic: Picture, x: number, y: number, w: number, index: number) {
  const tone = pic.tone === 'win' ? '#22c55e' : pic.tone === 'late' ? '#fb923c' : '#9ca3af';
  const gapMm = 0.8;
  const h = 21 + 5 * ((w - 6 - 4 * gapMm) / 5 + gapMm) + 2.5;
  p.rect(x, y, w, h, '#fff', 3, tone, pic.tone === 'unclaimed');
  p.font(2.9, '700');
  const lines = p.wrap(`Kuva ${index}. ${pic.title}`, w - 6);
  lines.slice(0, 2).forEach((l, i) => p.text(l, x + 3, y + 3 + i * 3.8));
  const k = bingoAfter(pic.cells, pic.drawn, pic.stage);
  p.font(2.4);
  p.text(`${stageLabel(pic.stage)} · arvottu ${pic.drawn.length}${k ? ` · bingon tuonut ${ballLabel(pic.drawn[k - 1]!)}` : ''}`, x + 3, y + 11.5, '#556');
  const drawn = new Set(pic.drawn);
  const full = new Set(rowMissing(pic.cells, drawn).flatMap((m, r) => (m === 0 ? [r] : [])));
  const bringer = k ? pic.drawn[k - 1] : null;
  const gap = 0.8;
  const cell = (w - 6 - 4 * gap) / 5;
  const gx = x + 3;
  let gy = y + 16;
  BINGO_COLUMNS.forEach((l, c) => {
    p.rect(gx + c * (cell + gap), gy, cell, 4, COLUMN[c]!, 0.8);
    p.font(2.8, '900');
    p.ctx.textAlign = 'center';
    p.text(l, gx + c * (cell + gap) + cell / 2, gy + 0.6, c === 2 || c === 4 ? '#111' : '#fff');
    p.ctx.textAlign = 'left';
  });
  gy += 5;
  pic.cells.forEach((n, i) => {
    const cx = gx + (i % 5) * (cell + gap);
    const cy = gy + Math.floor(i / 5) * (cell + gap);
    const rowFull = full.has(Math.floor(i / 5));
    const marked = n === FREE || drawn.has(n);
    p.rect(cx, cy, cell, cell, rowFull ? '#bbf7d0' : marked ? '#ede9fe' : '#fff', 0.8, rowFull ? '#22c55e' : '#c9d1e0');
    if (n === bringer) {
      p.ctx.strokeStyle = '#f97316';
      p.ctx.lineWidth = px(0.7);
      p.ctx.strokeRect(px(cx + 0.4), px(cy + 0.4), px(cell - 0.8), px(cell - 0.8));
    }
    p.font(3.2, '700');
    p.ctx.textAlign = 'center';
    p.text(n === FREE ? '★' : String(n), cx + cell / 2, cy + cell / 2 - 1.6);
    p.ctx.textAlign = 'left';
  });
  return h;
}

/** Draws the log on A4 pages and saves it as a PDF. */
export async function saveBingoLogPdf(log: BingoLogView): Promise<void> {
  await document.fonts?.ready;
  const { rows, winners, presses } = describe(log);
  const p = new Pages();
  const W = PAGE_W - 2 * M;

  p.font(7, '800');
  p.text('Areena Bingo – pelin loki', M, p.y);
  p.y += 10;
  const meta: [string, string][] = [
    ['Peli', `#${log.gameId} · ${log.mode === 'auto' ? 'automaattinen bingo' : 'juonnettu bingo'}`],
    ['Päivä', `${day(log.startedAt)}, alkoi ${clock(log.startedAt)}${log.endedAt ? `, päättyi ${clock(log.endedAt)}` : ''}`],
    ['Kuponkierät', log.labels],
    ['Mukana', `${log.coupons} kuponkia, ${log.players} pelaajaa`],
    ['Arvottu', `${log.draws.length}/75 numeroa · BINGO!-painalluksia ${presses}`],
  ];
  for (const [k, v] of meta) {
    p.font(3.4);
    p.text(k, M, p.y, '#556');
    p.font(3.4, '600');
    p.text(v, M + 34, p.y);
    p.y += 5;
  }

  p.heading('Voittajat');
  p.font(3.2, '700');
  const cols = [M + 2, M + 42, M + 92, M + 117, M + 145];
  ['Bingo', 'Voittaja', 'Kuponki', 'Numerolla', 'Aika'].forEach((h, i) => p.text(h, cols[i]!, p.y));
  p.y += 5.5;
  if (winners.length === 0) {
    p.font(3.2);
    p.text('Ei voittajia.', M + 2, p.y, '#556');
    p.y += 5.5;
  }
  for (const w of winners) {
    p.ensure(6);
    p.font(3.2);
    p.text(`${stageLabel(w.stage)}${w.jackpot ? ' 💎' : ''}`, cols[0]!, p.y);
    p.font(3.2, '700');
    p.text(w.nickname, cols[1]!, p.y);
    p.font(3.2);
    p.text(String(w.card), cols[2]!, p.y);
    p.text(`${w.after}.`, cols[3]!, p.y);
    p.text(clock(w.at), cols[4]!, p.y);
    p.y += 5.5;
  }

  p.heading('Arvotut numerot järjestyksessä');
  p.font(3.1);
  for (const line of p.wrap(log.draws.map((n, i) => `${i + 1}. ${ballLabel(n)}`).join(' · '), W)) {
    p.ensure(5);
    p.text(line, M, p.y);
    p.y += 4.6;
  }

  p.heading('Tapahtumat');
  p.font(2.8);
  for (const line of p.wrap(
    'Kuponkikuvat bingotilanteista ovat kunkin numeron tapahtumien perässä: violetti = arvottu, vihreä = täysi rivi, oranssi reuna = bingon tuonut numero. Kehys: vihreä = voittaja, harmaa katkoviiva = huutamaton bingo, oranssi = myöhässä (vanha bingo).',
    W,
  )) {
    p.text(line, M, p.y, '#445');
    p.y += 4;
  }
  p.y += 2;

  let pictureNo = 0;
  for (const row of rows) {
    if (Array.isArray(row)) {
      const w = 54;
      for (let i = 0; i < row.length; i += 3) {
        p.ensure(22 + w + 4);
        let h = 0;
        row.slice(i, i + 3).forEach((pic, j) => (h = Math.max(h, drawCoupon(p, pic, M + 20 + j * (w + 4), p.y, w, ++pictureNo))));
        p.y += h + 3;
      }
      continue;
    }
    const tag = TAG[row.tag]!;
    p.font(row.small ? 3 : 3.3, row.bold ? '700' : '400');
    const lines = p.wrap(row.text, W - 46);
    const h = Math.max(5, lines.length * (row.small ? 4 : 4.5) + 1.5);
    p.ensure(h);
    p.font(3, '400');
    p.text(clock(row.at), M, p.y + 0.6, row.small ? '#667' : '#1a2233');
    p.rect(M + 18, p.y + 0.2, 23, 4.2, tag.bg, 2);
    p.font(2.7, '700');
    p.ctx.textAlign = 'center';
    p.text(tag.label, M + 18 + 11.5, p.y + 0.9, tag.fg);
    p.ctx.textAlign = 'left';
    p.font(row.small ? 3 : 3.3, row.bold ? '700' : '400');
    lines.forEach((l, i) => p.text(l, M + 46, p.y + 0.6 + i * (row.small ? 4 : 4.5), row.small ? '#445' : '#1a2233'));
    p.y += h;
    p.rect(M, p.y - 0.4, W, 0.2, '#e3e7ef');
  }

  // Page numbers.
  p.pages.forEach((c, i) => {
    const ctx = c.getContext('2d')!;
    ctx.font = `400 ${px(2.6)}px ${SANS}`;
    ctx.fillStyle = '#889';
    ctx.textAlign = 'right';
    ctx.fillText(`Areena Bingo · peli #${log.gameId} · sivu ${i + 1}/${p.pages.length}`, px(PAGE_W - M), px(PAGE_H - 9));
  });

  const images: PdfImage[] = [];
  for (const c of p.pages) {
    const blob = await new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', 0.85));
    images.push({ jpeg: new Uint8Array(await blob.arrayBuffer()), pixelWidth: c.width, pixelHeight: c.height, x: 0, y: 0, width: PAGE_W, height: PAGE_H });
  }
  const pdf = buildPdf(images);
  const url = URL.createObjectURL(new Blob([pdf.buffer as ArrayBuffer], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `areena-bingo-loki-${log.gameId}-${new Date(log.startedAt).toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
